import { c as createFocusTrap } from './focus-trap.esm-C-N_-G0z.js';

/**
 * Age Gate Custom Element
 * Displays an age verification modal that checks if the user is of legal drinking age
 * Uses cookies to remember user's consent and supports redirect for underage users
 *
 * @class
 * @extends HTMLElement
 *
 * @example
 * <age-gate data-redirect-url="https://example.com" data-cookie-duration="30">
 *   <div data-content>
 *     <button data-consent>I'm 18+</button>
 *     <button data-decline>Under 18</button>
 *   </div>
 * </age-gate>
 */
class AgeGate extends HTMLElement {
  /** @type {HTMLElement | null} - Content container element */
  #content = this.querySelector('[data-content]');

  /** @type {HTMLButtonElement | null} - Consent button element */
  #consentButton = this.querySelector('[data-consent]');

  /** @type {HTMLButtonElement | null} - Decline button element */
  #declineButton = this.querySelector('[data-decline]');

  /** @type {import('focus-trap').FocusTrap | null} - Focus trap instance */
  #focusTrap = null;

  /**
   * Custom element lifecycle callback - called when element is connected to the DOM
   * @returns {void}
   */
  connectedCallback() {
    // Re-query elements in case they weren't available in constructor
    this.#content = this.querySelector('[data-content]');
    this.#consentButton = this.querySelector('[data-consent]');
    this.#declineButton = this.querySelector('[data-decline]');

    // Get settings from data attributes
    /** @type {string} - URL to redirect to when user declines */
    this.redirectUrl = this.dataset.redirectUrl || 'https://www.google.com';

    /** @type {number} - Cookie duration in days (0 = no cookie, age gate appears every time) */
    this.cookieDuration = parseInt(this.dataset.cookieDuration, 10);

    // Initialize focus trap
    if (this.#content) {
      this.#focusTrap = createFocusTrap(this.#content, {
        allowOutsideClick: true,
        escapeDeactivates: false,
        clickOutsideDeactivates: false,
      });
    }

    // Initialize
    this.#init();
  }

  /**
   * Custom element lifecycle callback - called when element is disconnected from the DOM
   * Cleans up focus trap to prevent memory leaks
   * @returns {void}
   */
  disconnectedCallback() {
    // Clean up
    if (this.#focusTrap) {
      this.#focusTrap.deactivate();
    }
  }

  /**
   * Initialize the age gate
   * Checks consent status and sets up event listeners
   * @private
   * @returns {void}
   */
  #init() {
    // Check if user has already consented
    if (!this.#hasConsented()) {
      this.#show();
    }

    // Set up event listeners
    this.#setupEventListeners();
  }

  /**
   * Set up event listeners for consent and decline buttons
   * @private
   * @returns {void}
   */
  #setupEventListeners() {
    if (this.#consentButton) {
      this.#consentButton.addEventListener('click', () => this.#handleConsent());
    }

    if (this.#declineButton) {
      this.#declineButton.addEventListener('click', () => this.#handleDecline());
    }
  }

  /**
   * Check if user has already consented by looking for the age gate cookie
   * @private
   * @returns {boolean} True if consent cookie exists, false otherwise
   */
  #hasConsented() {
    return document.cookie.split(';').some((cookie) => cookie.trim().startsWith('ageGate='));
  }

  /**
   * Set consent cookie with specified duration
   * If cookieDuration is 0, no cookie is set (age gate will appear on every page load)
   * @private
   * @returns {void}
   */
  #setConsentCookie() {
    // If duration is 0, don't set a cookie (age gate will reappear on every page load)
    if (this.cookieDuration === 0) {
      return;
    }

    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + this.cookieDuration);

    document.cookie = `ageGate=true; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`;
  }

  /**
   * Handle consent (user is over 18)
   * Sets cookie and hides the age gate
   * @private
   * @returns {void}
   */
  #handleConsent() {
    this.#setConsentCookie();
    this.#hide();
  }

  /**
   * Handle decline (user is under 18)
   * Redirects to the specified URL
   * @private
   * @returns {void}
   */
  #handleDecline() {
    // Redirect to specified URL
    window.location.href = this.redirectUrl;
  }

  /**
   * Show the age gate modal
   * Activates focus trap and locks body scroll
   * @private
   * @returns {void}
   */
  #show() {
    this.toggleAttribute('data-active', true);
    this.toggleAttribute('inert', false);

    // Lock body scroll
    document.documentElement.style.overflow = 'hidden';

    // Activate focus trap
    if (this.#focusTrap) {
      this.#focusTrap.activate();
    }
  }

  /**
   * Hide the age gate modal
   * Deactivates focus trap and unlocks body scroll after transition
   * @private
   * @returns {void}
   */
  #hide() {
    this.toggleAttribute('data-active', false);
    this.toggleAttribute('inert', true);

    document.documentElement.style.overflow = 'auto';

    // Deactivate focus trap
    if (this.#focusTrap) {
      this.#focusTrap.deactivate();
    }

    // Unlock body scroll after transition
    if (this.#content) {
      this.#content.addEventListener(
        'transitionend',
        () => {
          if (!this.hasAttribute('data-active')) {
            document.documentElement.style.overflow = '';
          }
        },
        { once: true }
      );
    } else {
      document.documentElement.style.overflow = '';
    }
  }
}

// Define custom element
customElements.define('age-gate', AgeGate);
